import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Collapse } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { ReactComponent as FavoriteSelectedIcon } from "assets/images/icons/icn_favorite_selected.svg";
import { ReactComponent as FavoriteUnselectedIcon } from "assets/images/icons/icn_favorite_unselected.svg";
import { CameraStatusIcon } from "./DeviceItem";
import { setCameraDragData } from "../../lib/cameraDrag";

/**
 * NvrCameraRow
 * Single camera row inside an NVR — same alignment as DeviceItem.
 */
const NvrCameraRow = ({ camera, isFavorited, onToggleFavorite, onPlay }) => {
  const theme = useTheme();
  const [favoriteHover, setFavoriteHover] = useState(false);
  const [isSelected, setIsSelected] = useState(Boolean(isFavorited));
  const online = camera?.linkStatus === "online";

  useEffect(() => {
    setIsSelected(Boolean(isFavorited));
  }, [isFavorited]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: "4px",
        py: "2px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          p: "4px",
          flex: 1,
          minWidth: 0,
          cursor: onPlay ? "pointer" : "default",
          borderRadius: "4px",
          "&:hover": onPlay ? { backgroundColor: "rgba(23,38,100,0.06)" } : undefined,
        }}
        draggable
        onDragStart={(e) => setCameraDragData(e, camera)}
        onClick={() => onPlay?.(camera)}
      >
        <CameraStatusIcon online={online} />
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: online ? theme.palette.primary.main : theme.palette.grey[500],
          }}
        >
          {camera.name}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          size="small"
          disableRipple
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setIsSelected((prev) => !prev);
            onToggleFavorite?.(camera.id);
          }}
          onMouseEnter={() => setFavoriteHover(true)}
          onMouseLeave={() => setFavoriteHover(false)}
          sx={{
            p: "4px",
            backgroundColor: favoriteHover ? "rgba(23,38,100,0.08)" : "transparent",
            transition: "all 0.2s",
            borderRadius: "4px",
          }}
        >
          {isSelected ? (
            <FavoriteSelectedIcon width={18} height={18} />
          ) : (
            <FavoriteUnselectedIcon width={18} height={18} style={{ opacity: favoriteHover ? 1 : 0.6 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

/**
 * NvrItem
 * Collapsible NVR device row with its camera list.
 *
 * Props:
 *   nvr              - { id, name, cameras: [{ id, name, favorited?, linkStatus? }] }
 *   onToggleFavorite - called with camera id
 *   onPlay           - called with camera when row is clicked
 */
const NvrItem = ({ nvr, onToggleFavorite, onPlay, isFirst, isLast }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const GAP = 15; // must match pt on scroll container in index.jsx

  return (
    <Box sx={{ pb: "8px", position: "relative", pl: "22px" }}>
      {/* Vertical incoming line: extends up into the gap for the first item */}
      <Box sx={{ position: "absolute", left: "8px", top: isFirst ? `-${GAP}px` : 0, height: isFirst ? `${GAP + 18}px` : "18px", width: "2px", backgroundColor: theme.palette.primary.main }} />
      {/* Horizontal connector (─ part of L) */}
      <Box sx={{ position: "absolute", left: "10px", top: "16px", width: "12px", height: "2px", backgroundColor: theme.palette.primary.main }} />
      {/* Continuing vertical line below connector — only for non-last NVR items */}
      {!isLast && (
        <Box sx={{ position: "absolute", left: "8px", top: "18px", bottom: 0, width: "2px", backgroundColor: theme.palette.primary.main }} />
      )}
      {/* NVR sub-header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "4px",
          py: "2px",
          backgroundColor: theme.palette.primary.background,
          border: `1px solid rgba(0,0,0,0.12)`,
          cursor: "pointer",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", p: "4px" }}>
          <StorageOutlinedIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.palette.primary.main }}>
            {nvr.name} ({nvr.cameras.length})
          </Typography>
        </Box>
        <IconButton size="small" disableRipple sx={{ p: "4px" }}>
          {expanded ? (
            <ArrowDropDownIcon sx={{ fontSize: 20, color: theme.palette.primary.dark }} />
          ) : (
            <ArrowRightIcon sx={{ fontSize: 20, color: theme.palette.primary.dark }} />
          )}
        </IconButton>
      </Box>

      {/* Camera rows */}
      <Collapse in={expanded}>
        <Box sx={{ ml: "10px", maxHeight: 280, overflowY: "auto" }}>
          {nvr.cameras.map((camera) => (
            <NvrCameraRow
              key={camera.id}
              camera={camera}
              isFavorited={camera.favorited === true}
              onToggleFavorite={onToggleFavorite}
              onPlay={onPlay}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default NvrItem;
